import clsx, { ClassValue } from "clsx";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ConfigData } from "@/api/config";
import { Order } from "@/api/verify";
import { CalculatedReturn, ErrorType, ReturnOrder } from "@/types";

export const setFavicon = (src: string) => {
  const favicon = document.querySelector(
    'link[rel*="icon"]'
  ) as HTMLLinkElement;
  favicon.href = src;
};

export const setDocumentTitle = (text: string) => {
  document.title = text;
};

export const getConfigFileName = () => {
  const subDomain = window.location.hostname.split(".")[0];
  const langParam = window.location.search.match(/lang=([\w-]+)/);

  return `${subDomain}${langParam ? `-${langParam[1]}` : ""}`;
};

export const cn = (...classes: ClassValue[]) => twMerge(clsx(...classes));

// https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
const MAX_ENCODED_CONFIG_LENGTH = 20_000;

export const decodeConfig = (configParamValue: string) => {
  if (configParamValue.length > MAX_ENCODED_CONFIG_LENGTH) {
    throw new Error("Config override is too large");
  }

  const decodedParam = decodeURIComponent(configParamValue);
  const binaryString = atob(decodedParam);

  return JSON.parse(
    new TextDecoder().decode(
      Uint8Array.from(binaryString, (char) => char.charCodeAt(0))
    )
  );
};

export const formatPercentage = (value: number) =>
  new Intl.NumberFormat(undefined, {
    style: "percent",
  }).format(value);

export const formatCurrency = (
  value: number,
  currency: string | undefined = undefined,
  decimalPlaces: number = 2
) =>
  new Intl.NumberFormat(undefined, {
    style: currency ? "currency" : undefined,
    currency: currency,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);

const canReturnItemBeExchanged = (returnItemId: string, orderData: Order) => {
  if (!orderData.exchange_data) return false;
  const returnItem = orderData.line_items.find(
    (item) => item.id === returnItemId
  );

  if (!returnItem || !returnItem.variant_id) return false;

  const exchangeOptions =
    orderData.exchange_data.options[returnItem.variant_id];

  if (
    !Object.keys(orderData.exchange_data.options).includes(
      returnItem.variant_id.toString()
    )
  )
    return false;

  if (exchangeOptions.length === 0) return false;

  // In case of allowing exchanges to same variant but that's the only option, don't show exchanges
  if (
    exchangeOptions.length === 1 &&
    exchangeOptions[0] === returnItem.variant_id
  )
    return false;

  return true;
};

export const shouldShowRefundMethod = (
  returnOrder: ReturnOrder,
  orderData: Order,
  calculatedReturn: CalculatedReturn | undefined
) => {
  return (
    orderData.gift_card_value_multiplier &&
    returnOrder.line_items.some((item) => !item.exchange_variant_id) &&
    ((calculatedReturn?.refund_value.presentment_money.amount ?? 0) > 0 ||
      calculatedReturn === undefined)
  );
};

export const getNextRoute = (
  returnItemId: string,
  orderData: Order,
  returnOrder: ReturnOrder,
  action: "returnReason" | "exchange",
  calculatedReturn: CalculatedReturn | undefined = undefined
): string => {
  const currentReturnItemIndex = returnOrder.line_items.findIndex(
    (item) => item.id === returnItemId
  );
  const nextReturnItemIndex =
    currentReturnItemIndex < returnOrder.line_items.length - 1
      ? currentReturnItemIndex + 1
      : -1;

  if (action === "returnReason") {
    if (canReturnItemBeExchanged(returnItemId, orderData))
      return `/return-item/${returnItemId}/exchange`;

    if (nextReturnItemIndex === -1 && orderData.exchange_data?.upsell_enabled)
      return `/upsell-offer`;

    if (nextReturnItemIndex !== -1)
      return `/return-item/${returnOrder.line_items[nextReturnItemIndex].id}/return-reason`;
  } else {
    if (nextReturnItemIndex !== -1)
      return `/return-item/${returnOrder.line_items[nextReturnItemIndex].id}/return-reason`;
    else if (
      orderData.exchange_data?.upsell_enabled &&
      returnOrder.line_items.some((item) => !item.exchange_variant_id)
    )
      return `/upsell-offer`;
  }

  if (shouldShowRefundMethod(returnOrder, orderData, calculatedReturn))
    return "/refund-method";
  return "/confirm";
};

export const useDebounce = <T>(value: T, delay: number = 1000): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const getErrorMessage = (error: Error, configData: ConfigData) => {
  switch (error.message) {
    case ErrorType.InvalidOrder:
      return configData.invalid_order_message;
    case ErrorType.OrderNotFulfilled:
      return configData.order_not_fulfilled_message;
    case ErrorType.OrderTooOld:
      return configData.order_too_old_message;
    case ErrorType.OrderAlreadyHasReturn:
      return configData.order_already_has_return_message;
    default:
      return configData.default_error_message;
  }
};
